import { EditorState } from "draft-js";
import * as yup from "yup";
import { findDayGaps, hasOverlap } from "./validationFns";
import { DateLike } from "./validationFns";

export const createValidationSchema = (
  isEditMode: boolean = false,
  start: DateLike,
  end: DateLike
) =>
  yup.object().shape({
    dateEvent: yup
      .date()
      // .transform((value, originalValue) => {
      //   if (!originalValue) return null;
      //   return originalValue.toDate ? originalValue.toDate() : originalValue;
      // })
      .typeError("Введите корректную дату")
      .min(isEditMode ? new Date(1900, 0, 1) : new Date(), "Дата проведения не может быть в прошлом")
      .required("Укажите дату мероприятия"),
    startDateAcceptingApplications: yup
      .date()
      // .transform((value, originalValue) => {
      //   if (!originalValue) return null;
      //   return originalValue.toDate ? originalValue.toDate() : originalValue;
      // })
      .typeError("Введите корректную дату")
      .max(
        yup.ref("dateEvent"),
        "Дата начала приема заявок не может быть позже даты мероприятия"
      )
      .required("Укажите дату начала приема заявок"),

    endDateAcceptingApplications: yup
      .date()
      // .transform((value, originalValue) => {
      //   if (!originalValue) return null;
      //   return originalValue.toDate ? originalValue.toDate() : originalValue;
      // })
      .typeError("Введите корректную дату")
      .max(
        yup.ref("dateEvent"),
        "Дата окончания приема заявок не может быть после даты мероприятия"
      )
      .min(
        yup.ref("startDateAcceptingApplications"),
        "Дата конца приема заявок должна быть позже даты начала"
      )
      .required("Укажите дату конца приема заявок"),

    participantNumbersPrintDate: yup
      .date()
      // .transform((value, originalValue) => {
      //   if (!originalValue) return null;
      //   return originalValue.toDate ? originalValue.toDate() : originalValue;
      // })
      .typeError("Введите корректную дату")
      .max(
        yup.ref("dateEvent"),
        "Дата печати номеров участников не может быть позже даты мероприятия"
      )
      .min(
        yup.ref("startDateAcceptingApplications"),
        "Дата печати номеров участников не может быть раньше даты начала приема заявок"
      )
      .required("Укажите дату печати номеров участников"),
    translations: yup.array().of(
      yup.object({
        logo: isEditMode
          ? yup.mixed().nullable()
          : yup
            .mixed()
            .required("Логотип обязателен")
            .test("file-format", "Разрешены только файлы формата PNG, JPEG, JPG, SVG", (value) => {
              if (typeof value === "string") return true;
              if (!value) return false;
              if (value instanceof File) {
                const allowedMime = ["image/png", "image/jpeg", "image/svg+xml"];
                const allowedExt = ["png", "jpg", "jpeg", "svg"];
                const mimeOk = !value.type || allowedMime.includes(value.type);
                const ext = value.name.split(".").pop()?.toLowerCase();
                const extOk = !!ext && allowedExt.includes(ext);
                return mimeOk || extOk;
              }
              if (value && typeof value === "object" && "name" in value) {
                return true; // Для существующих файлов
              }
              return false;
            })
            .test("file-size", "Размер файла не должен превышать 20 МБ", (value) => {
              if (typeof value === "string") return true;
              if (!value) return false;
              if (value instanceof File) {
                return value.size <= 20 * 1024 * 1024;
              }
              return true; // Для существующих файлов
            }),
        name: yup.string().required("Укажите название"),
        place: yup.string().required("Укажите место проведения"),
        startKitMessage: yup
          .string()
          .required("Укажите состав стартового пакета"),
        text: yup
          .mixed()
          .required("Добавьте описание")
          .test("is-valid-text", "Добавьте описание", (value) => {
            if (value instanceof EditorState) {
              return !!value.getCurrentContent()?.getPlainText().trim();
            }
            if (typeof value === "string") {
              return !!value.trim();
            }
            return false;
          }),
      })
    ),
    partners: yup.array().of(
      yup.object({
        translations: yup.array().of(
          yup.object({
            name: yup.string().required("Укажите название партнера"),
          })
        ),
      })
    ),
    publicOffer: yup.array().of(
      yup.object({
        file: isEditMode
          ? yup.mixed().nullable()
          : yup
            .mixed()
            .required("Публичная оферта обязательна")
            .test(
              "file-format",
              "Неверный формат файла. Разрешены только PDF и DOCX",
              (value) => {
                if (typeof value === "string") return true;
                if (!value) return false;

                if (value instanceof File) {
                  const extension = value.name
                    .split(".")
                    .pop()
                    ?.toLowerCase();
                  return extension === "pdf" || extension === "docx";
                }

                if (value instanceof FileList && value.length > 0) {
                  const file = value[0];
                  const extension = file.name.split(".").pop()?.toLowerCase();
                  return extension === "pdf" || extension === "docx";
                }

                return false;
              }
            ),

        languageId: yup.number().required(),
      })
    ),
    documents: isEditMode
      ? yup.array().of(yup.mixed().nullable())
      : yup.array().of(
        yup
          .mixed()
          .required("Документы обязательны")
          .test(
            "file-format",
            "Неверный формат файла. Разрешены только PDF и DOCX",
            (value) => {
              if (typeof value === "string") return true;
              if (!value) return false;
              if (
                value &&
                typeof value === "object" &&
                value !== null &&
                "name" in value
              ) {
                const fileName = value.name;
                const fileExtension = fileName
                  .split(".")
                  .pop()
                  ?.toLowerCase();
                return fileExtension === "pdf" || fileExtension === "docx";
              }
              return false;
            }
          )
      ).min(1, "Добавьте хотя бы один файл"),
    insurancePrice: yup
      .number()
      .transform((value, originalValue) =>
        originalValue === "" || originalValue === null
          ? undefined
          : Number(originalValue)
      )
      .typeError("Укажите стоимость страховки")
      .min(1, "Минимальная стоимость страховки: 1")
      .required("Укажите стоимость страховки"),
    distances: yup
      .array()
      .of(
        yup.object().shape({
          hasInsurance: yup.bool().required("Наличие страховки обязательно"),
          name: yup.string().required("Укажите название дистанции"),
          startNumberRanges: yup
            .array()
            .of(
              yup.object().shape({
                startNumbersFrom: yup
                  .number()
                  .transform((value, originalValue) =>
                    originalValue === "" || originalValue === null
                      ? undefined
                      : Number(originalValue)
                  )
                  .typeError("Укажите число")
                  .min(1, "Начальный номер не может быть меньше 1")
                  .required("Укажите начальный номер"),

                startNumbersTo: yup
                  .number()
                  .transform((value, originalValue) =>
                    originalValue === "" || originalValue === null
                      ? undefined
                      : Number(originalValue)
                  )
                  .nullable()
                  .typeError("Укажите число")
                  .min(1, "Конечный номер не может быть меньше 1")
                  .required("Укажите конечный номер")
                  .test(
                    "is-greater-or-equal",
                    "Конечный номер не должен быть меньше начального",
                    function (value) {
                      const { startNumbersFrom } = this.parent;
                      if (
                        typeof startNumbersFrom === "number" &&
                        typeof value === "number"
                      ) {
                        return value >= startNumbersFrom;
                      }
                      return true;
                    }
                  ),
              })
            )
            .min(1, "Укажите диапазон стартовых номеров")
            .test(
              "unique-ranges",
              "Диапазоны номеров не должны пересекаться",
              function (value) {
                if (!value || value.length < 2) return true;

                const ranges = value.map((range, index) => ({
                  from: range.startNumbersFrom,
                  to: range.startNumbersTo,
                  index,
                }));

                for (let i = 0; i < ranges.length; i++) {
                  for (let j = i + 1; j < ranges.length; j++) {
                    const range1 = ranges[i];
                    const range2 = ranges[j];

                    if (
                      range1.from === undefined ||
                      range1.to === null ||
                      range1.to === undefined ||
                      range2.from === undefined ||
                      range2.to === null ||
                      range2.to === undefined
                    ) {
                      continue;
                    }

                    if (range1.from <= range2.to && range1.to >= range2.from) {
                      return this.createError({
                        message: `Диапазоны номеров пересекаются: ${range1.from}-${range1.to} и ${range2.from}-${range2.to}`,
                      });
                    }
                  }
                }

                return true;
              }
            ),
          distancePrices: yup
            .array()
            .of(
              yup.object({
                dateStart: yup
                  .date()
                  .typeError("Введите корректную дату")
                  .required("Укажите дату начала"),
                dateEnd: yup
                  .date()
                  .typeError("Введите корректную дату")
                  .required("Укажите дату окончания"),
                price: yup
                  .number()
                  .min(0)
                  .typeError("Укажите цену периода")
                  .required("Укажите цену периода"),
              })
            )
            .min(1, "Хотя бы один период регистрации должен быть открыт")

            .test(
              "unique-price-periods",
              "Периоды регистрации не должны пересекаться",
              (arr: any) =>
                !Array.isArray(arr) || arr.length < 2 ? true : !hasOverlap(arr)
            )
            .test(
              "all-periods-covered",
              "Стоимость участия установлена не для всех периодов",
              function (periods: any) {
                if (!Array.isArray(periods) || !start || !end) return true;

                const { gaps } = findDayGaps(periods, start, end, {
                  allowTouching: true,
                });
                return gaps.length === 0;
              }
            ),
          true: yup
            .array()
            .of(
              yup.object({
                gender: yup.boolean().required(),
                ageFrom: yup
                  .number()
                  .typeError("Укажите возраст")
                  .min(0, "Минимальный возраст: 0")
                  .max(90, "Максимальный возраст: 90")
                  .required("Укажите возраст"),
                ageTo: yup
                  .number()
                  .typeError("Укажите возраст")
                  .min(0, "Минимальный возраст: 0")
                  .max(90, "Максимальный возраст: 90")
                  .required("Укажите возраст")
                  .test(
                    "is-greater-than",
                    "Возраст 'До' должен быть больше возраста 'От'",
                    function (value) {
                      const { ageFrom } = this.parent;
                      if (
                        typeof ageFrom === "number" &&
                        typeof value === "number"
                      ) {
                        return value > ageFrom;
                      }
                      return true;
                    }
                  ),
              })
            )
            .min(1, "Укажите возрастные категории для мужчин")
            .test(
              "unique-age-ranges-male",
              "Возрастные группы для мужчин не должны пересекаться",
              function (value) {
                if (!value || value.length < 2) return true;

                const ranges = value.map((range, index) => ({
                  from: range.ageFrom,
                  to: range.ageTo,
                  index,
                }));

                for (let i = 0; i < ranges.length; i++) {
                  for (let j = i + 1; j < ranges.length; j++) {
                    const range1 = ranges[i];
                    const range2 = ranges[j];

                    if (
                      range1.from === undefined ||
                      range1.to === undefined ||
                      range2.from === undefined ||
                      range2.to === undefined
                    ) {
                      continue;
                    }

                    if (range1.from <= range2.to && range1.to >= range2.from) {
                      return this.createError({
                        message: `Возрастные группы для мужчин пересекаются: ${range1.from}-${range1.to} и ${range2.from}-${range2.to}`,
                      });
                    }
                  }
                }

                return true;
              }
            ),
          false: yup
            .array()
            .of(
              yup.object({
                gender: yup.boolean().required(),
                ageFrom: yup
                  .number()
                  .typeError("Укажите возраст")
                  .min(0, "Минимальный возраст: 0")
                  .max(90, "Максимальный возраст: 90")
                  .required("Укажите возраст"),
                ageTo: yup
                  .number()
                  .typeError("Укажите возраст")
                  .min(0, "Минимальный возраст: 0")
                  .max(90, "Максимальный возраст: 90")
                  .required("Укажите возраст")
                  .test(
                    "is-greater-than",
                    "Возраст 'До' должен быть больше возраста 'От'",
                    function (value) {
                      const { ageFrom } = this.parent;
                      if (
                        typeof ageFrom === "number" &&
                        typeof value === "number"
                      ) {
                        return value > ageFrom;
                      }
                      return true;
                    }
                  ),
              })
            )
            .min(1, "Укажите возрастные категории для женщин")
            .test(
              "unique-age-ranges-female",
              "Возрастные группы для женщин не должны пересекаться",
              function (value) {
                if (!value || value.length < 2) return true;

                const ranges = value.map((range, index) => ({
                  from: range.ageFrom,
                  to: range.ageTo,
                  index,
                }));

                for (let i = 0; i < ranges.length; i++) {
                  for (let j = i + 1; j < ranges.length; j++) {
                    const range1 = ranges[i];
                    const range2 = ranges[j];

                    if (
                      range1.from === undefined ||
                      range1.to === undefined ||
                      range2.from === undefined ||
                      range2.to === undefined
                    ) {
                      continue;
                    }

                    if (range1.from <= range2.to && range1.to >= range2.from) {
                      return this.createError({
                        message: `Возрастные группы для женщин пересекаются: ${range1.from}-${range1.to} и ${range2.from}-${range2.to}`,
                      });
                    }
                  }
                }

                return true;
              }
            ),
        })
      )
      .test(
        "all-periods-covered-per-distance",
        "Стоимость участия установлена не для всех периодов",
        function (obj: any) {
          const winStart = yup.ref("startDateAcceptingApplications");
          const winEnd = yup.ref("endDateAcceptingApplications");
          const distances = obj?.distances ?? [];
          if (!winStart || !winEnd || !Array.isArray(distances)) return true;

          for (let i = 0; i < distances.length; i++) {
            const arr = distances[i]?.distancePrices ?? [];
            if (!Array.isArray(arr) || arr.length === 0) {
              return this.createError({
                path: `distances[${i}].distancePrices`,
                message: "Хотя бы один период регистрации должен быть открыт",
              });
            }
            const { gaps } = findDayGaps(arr, winStart, winEnd);
            if (gaps.length > 0) {
              return this.createError({
                path: `distances[${i}].distancePrices`,
                message: "Стоимость участия установлена не для всех периодов",
              });
            }
          }
          return true;
        }
      ),
  });

// export const validationSchema = createValidationSchema(false);
